import React, { useState, Fragment, useEffect, useMemo } from 'react'

import {
  Box,
  Fab,
  Grid,
  IconButton,
  Input,
  Menu,
  MenuItem,
  makeStyles,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import { styles } from './style'
import { useEditSequenceContext } from '../../../context/EditSequenceContext'
import { TimingSelect } from '../TimingSelect'
import {
  calculateTimingInSeconds,
  calculateTimingType,
  calculateTimingValue,
} from '../../../../../../utils'
import { EmailDialog } from '../../../../../../../../../../../Components/EmailDialog/EmailDialog'
import { sendActionSimulation } from '../../../../../../gql'
import { validPromofyEmail } from '../../../../../../../../../../../shared/validation'
import { ACTION } from '../../../../../../../../../../../shared/constants'
import { PhoneNumberDialog } from '../../../../../../../../../../../Components/PhoneNumberDialog/PhoneNumberDialog'
import { CONFIG } from '../../../../../../../../../../../Config/config'

const useStyles = makeStyles(() => styles)

export const SmsAndNotificationTemplate = ({
  commId,
  actionId,
  template,
  action,
  onDelete,
  controlModal,
  handleDrawerToggle,
  totalActions,
  index,
  commType,
  seqStatus,
}) => {
  const isDraft = isNaN(actionId)
  const isSendTestMessageDisabled = isDraft || seqStatus === 'ARCHIVED'
  const {
    saveOrUpdateAction,
    editSequenceContextState,
  } = useEditSequenceContext()
  const {
    sequence: { DateSequenceTemplate, hasScheduledTrigger, Trigger },
  } = editSequenceContextState
  let timingTypeLabelText =
    DateSequenceTemplate.length > 0 ? 'after scheduled start time' : ''
  if (!timingTypeLabelText && Trigger.length > 0) {
    timingTypeLabelText = hasScheduledTrigger
      ? 'before trigger'
      : 'after trigger'
  }

  const isNotification = useMemo(() => commType === 'notification', [commType])

  const classes = useStyles()

  const timeDelay = useMemo(
    () => (hasScheduledTrigger ? action.time_before : action.time_delay),
    [hasScheduledTrigger, action.time_before, action.time_delay],
  )
  const [openSendTestDialog, setOpenSendTestDialog] = useState(false)
  const [commInputError, setCommInputError] = useState('')
  const [commInputSuccess, setCommInputSuccess] = useState('')
  const [testCommunication, setTestCommunication] = useState('')
  const [isLoadingTestCommunication, setIsLoadingTestCommunication] = useState(
    false,
  )

  const [timingType, setTimingType] = useState(calculateTimingType(timeDelay))
  const [timingValue, setTimingValue] = useState(
    calculateTimingValue(timeDelay),
  )

  const timing = useMemo(() => {
    return calculateTimingInSeconds({ timingType, timingValue })
  }, [timingType, timingValue])

  const [anchorEl, setAnchorEl] = React.useState(null)

  const { name, title } = template || {
    'name': '',
    'subject': '',
    'body': '',
  }

  const open = Boolean(anchorEl)
  const ITEM_HEIGHT = 48

  const handleClick = event => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const onChangeCommInput = (value, error) => {
    if (commType === 'notification') {
      if (error) {
        setCommInputError(error)
      } else {
        const isNotPromofyEmail = validPromofyEmail(
          value,
          'Please provide @oppizi.com email',
        )
        setCommInputError(isNotPromofyEmail)
      }
    } else {
      setCommInputError(error)
    }
    setTestCommunication(value)
  }

  const closeEmailDialog = () => {
    setOpenSendTestDialog(false)
    setTimeout(() => {
      setCommInputSuccess('')
      setCommInputError('')
      setTestCommunication('')
    }, 1000)
  }

  const sendTestNotification = async () => {
    try {
      setIsLoadingTestCommunication(true)
      await sendActionSimulation({
        action: isNotification ? ACTION.PUSH_NOTIFICATION : ACTION.SMS,
        actionId,
        receiver: testCommunication,
      })
      setIsLoadingTestCommunication(false)
      setCommInputError('')
      setCommInputSuccess('Message sent')
    } catch (error) {
      console.error(error)
      setIsLoadingTestCommunication(false)
      setCommInputError(error.message)
      setCommInputSuccess('')
    }
  }

  useEffect(() => {
    saveOrUpdateAction({ actionId, timeDelay: String(timing) })
  }, [timing])

  useEffect(() => {
    setTimingType(calculateTimingType(timeDelay))
    setTimingValue(calculateTimingValue(timeDelay))
  }, [timeDelay])

  const popupOptions = [
    {
      disabled: false,
      id: 1,
      label: 'Edit',
      func: () => {
        handleClose()
        controlModal({
          eId: commId,
          aId: actionId,
          open: true,
          cType: commType,
        })
      },
    },
    {
      disabled: false,
      id: 2,
      label: 'Delete',
      func: () => {
        handleClose()
        onDelete(actionId, commType)
      },
    },
    {
      disabled: isSendTestMessageDisabled,
      id: 3,
      label: 'Send test message',
      func: () => {
        handleClose()
        setOpenSendTestDialog(true)
      },
    },
  ]
  
  return (
    <Fragment>
      {isNotification ? (
        <EmailDialog
          isOpen={openSendTestDialog}
          isLoading={isLoadingTestCommunication}
          error={Boolean(commInputError)}
          helperText={commInputError || commInputSuccess}
          onClose={closeEmailDialog}
          title="Send Test Push Notification"
          contentText="Please provide test user's email"
          onSend={sendTestNotification}
          onCancel={closeEmailDialog}
          onChange={onChangeCommInput}
          leftButtonLabel="Cancel"
          rightButtonLabel="Send"
          cypress_id="EP-send-test-notification-email-dialog"
          succeed={Boolean(commInputSuccess)}
          value={testCommunication}
        />
      ) : (
        <PhoneNumberDialog
          allowedCountryCodes={CONFIG.SMS.ALLOWED_COUNTRY_CODES}
          isOpen={openSendTestDialog}
          isLoading={isLoadingTestCommunication}
          error={Boolean(commInputError)}
          helperText={commInputError || commInputSuccess}
          onClose={closeEmailDialog}
          title="Send Test SMS Notification"
          contentText="Please provide test user's phone number"
          onSend={sendTestNotification}
          onCancel={closeEmailDialog}
          onChange={onChangeCommInput}
          leftButtonLabel="Cancel"
          rightButtonLabel="Send"
          cypress_id="EP-send-test-notification-phone-number-dialog"
          succeed={Boolean(commInputSuccess)}
          value={testCommunication}
        />
      )}
      <Grid container direction="column" alignItems="center">
        <div>
          <Grid item xs>
            <div className={classes.line} />
          </Grid>
        </div>
        <Grid item>
          <div className={classes.header}>
            {`${isNotification ? 'Notification' : 'Sms'} ${
              isDraft ? '[DRAFT]' : ''
            }`}
            <div>
              <IconButton onClick={handleClick}>
                <MoreVertIcon style={{ fill: 'white' }} />
              </IconButton>
              <Menu
                id="long-menu"
                anchorEl={anchorEl}
                keepMounted
                open={open}
                onClose={handleClose}
                PaperProps={{
                  style: {
                    maxHeight: ITEM_HEIGHT * 4.5,
                    width: '9em',
                  },
                }}
              >
                {popupOptions.map(item => (
                  <MenuItem
                    disabled={item.disabled}
                    key={item.label}
                    onClick={() => item.func()}
                  >
                    <div className={classes.popupItem}>{item.label}</div>
                  </MenuItem>
                ))}
              </Menu>
            </div>
          </div>
        </Grid>
        <Grid item>
          <div className={classes.body}>
            <Grid container direction="column">
              <div className={classes.wrapperInput}>
                <Box>Name:</Box>
                <Input
                  value={name}
                  size="small"
                  className={classes.input}
                  variant="filled"
                  fullWidth
                  disableUnderline={true}
                  disabled={true}
                />
              </div>
              {isNotification && (
                <div className={classes.wrapperInput}>
                  <Box>Title:</Box>
                  <Input
                    size="small"
                    className={classes.input}
                    variant="filled"
                    fullWidth
                    disableUnderline={true}
                    disabled={true}
                    value={title}
                  />
                </div>
              )}

              <TimingSelect
                timingTypeLabelText={timingTypeLabelText}
                timingType={timingType}
                setTimingType={setTimingType}
                timingValue={timingValue}
                setTimingValue={setTimingValue}
              />
            </Grid>
          </div>
        </Grid>
        <Grid item xs>
          <div className={classes.line} />
        </Grid>
        {totalActions - 1 == index ? (
          <Grid item xs>
            <Fab
              color="primary"
              aria-label="add"
              onClick={() => handleDrawerToggle(true)}
            >
              <AddIcon />
            </Fab>
          </Grid>
        ) : null}
      </Grid>
    </Fragment>
  )
}
